import React, { useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import {
  ASSEMBLY_DETAIL_COLUMNS,
  ASSEMBLY_PRIMARY_COLUMNS,
  DEFAULT_ASSEMBLY_COLUMN_VISIBILITY,
} from './assemblyScheduleConfig';

const AssemblyViewControls = ({
  columnVisibility,
  savedViews,
  isSavingView,
  onColumnVisibilityChange,
  onSaveView,
  onLoadView,
  onDeleteView,
}) => {
  const [showColumns, setShowColumns] = useState(false);
  const [showSaveView, setShowSaveView] = useState(false);
  const [showLoadView, setShowLoadView] = useState(false);
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');

  const updateColumn = (key, checked) => {
    onColumnVisibilityChange((current) => ({ ...current, [key]: checked }));
  };

  const setAllColumns = (visible) => {
    onColumnVisibilityChange(
      Object.fromEntries(Object.keys(DEFAULT_ASSEMBLY_COLUMN_VISIBILITY).map((key) => [key, visible]))
    );
  };

  const handleSave = async () => {
    if (!viewName.trim()) return;
    const saved = await onSaveView({
      name: viewName.trim(),
      description: viewDescription.trim(),
    });
    if (saved) {
      setViewName('');
      setViewDescription('');
      setShowSaveView(false);
    }
  };

  const renderColumnGroup = (title, columns) => (
    <Col xs={12} lg={6}>
      <h6>{title}</h6>
      <div className="assembly-column-options">
        {columns.map((column) => (
          <Form.Check
            key={column.key}
            id={`assembly-column-${column.key}`}
            type="checkbox"
            label={column.label}
            checked={columnVisibility[column.key] !== false}
            onChange={(event) => updateColumn(column.key, event.target.checked)}
          />
        ))}
      </div>
    </Col>
  );

  return (
    <>
      <div className="d-flex flex-wrap gap-2">
        <Button variant="outline-secondary" onClick={() => setShowColumns(true)}>
          <i className="bi bi-layout-three-columns me-2" />
          Columns
        </Button>
        <Button variant="outline-success" onClick={() => setShowSaveView(true)}>
          <i className="bi bi-floppy me-2" />
          Save View
        </Button>
        <Button variant="outline-primary" onClick={() => setShowLoadView(true)}>
          <i className="bi bi-folder-open me-2" />
          Load View
        </Button>
      </div>

      <Modal show={showColumns} onHide={() => setShowColumns(false)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Assembly Schedule Columns</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 mb-3">
            <Button size="sm" variant="outline-primary" onClick={() => setAllColumns(true)}>Show all</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setAllColumns(false)}>Hide all</Button>
          </div>
          <Row className="g-4">
            {renderColumnGroup('Primary row', ASSEMBLY_PRIMARY_COLUMNS)}
            {renderColumnGroup('Expanded row', ASSEMBLY_DETAIL_COLUMNS)}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowColumns(false)}>Done</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSaveView} onHide={() => setShowSaveView(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Save Assembly View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="assembly-view-name">
            <Form.Label>View name</Form.Label>
            <Form.Control
              value={viewName}
              onChange={(event) => setViewName(event.target.value)}
              placeholder="Example: Assembly team default"
              autoFocus
            />
          </Form.Group>
          <Form.Group controlId="assembly-view-description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={viewDescription}
              onChange={(event) => setViewDescription(event.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveView(false)}>Cancel</Button>
          <Button
            variant="success"
            onClick={handleSave}
            disabled={!viewName.trim() || isSavingView}
          >
            {isSavingView ? 'Saving...' : 'Save View'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showLoadView} onHide={() => setShowLoadView(false)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Load Assembly View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {savedViews.length === 0 ? (
            <p className="text-muted mb-0">No saved Assembly Schedule views were found.</p>
          ) : (
            <div className="assembly-saved-view-list">
              {savedViews.map((view) => (
                <div className="assembly-saved-view" key={view.ID || view.id || view.Key}>
                  <div>
                    <strong>{view.Key}</strong>
                    {view.Description && <div className="text-muted small">{view.Description}</div>}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        onLoadView(view);
                        setShowLoadView(false);
                      }}
                    >
                      Load
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => onDeleteView(view)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AssemblyViewControls;
